import { isNil, isNotNil, isString } from '@flowio/is';
import { Nullable } from '../types';
import { DEFAULT_AUTHORIZATION_TIMEOUT_MS, INLINE_CALLBACK_MESSAGE_TYPE, CALLBACK_ORIGIN } from '../constants';
import type { InlineCallbackWrappedMessage } from '../types';
import getRollbarInstance from './getRollbarInstance';

type InlineCallbackListenerOptions = {
  expiresAt?: string;
  polling?: boolean;
};

export function getTimeoutMsFromNow(expiresAt?: string): number {
  if (isString(expiresAt)) {
    const parsedDate = Date.parse(expiresAt);
    if (!Number.isNaN(parsedDate)) {
      const timeoutMs = parsedDate - Date.now();
      if (timeoutMs < 5000) { // sanity check, should be at least 5 seconds.
        getRollbarInstance().warn('@flowio/payment-sdk: Inline callback listener expiration time is less than 5 seconds.');
      }
      return timeoutMs;
    }
  }
  return DEFAULT_AUTHORIZATION_TIMEOUT_MS;
}

export default function createInlineCallbackListener<T = unknown>(
  options: InlineCallbackListenerOptions,
): Promise<T> {
  return new Promise((resolve, reject) => {
    let timeoutId: Nullable<number> | null = null;
    const messageHandler = (event: MessageEvent<string>): void => {
      if (event.origin !== CALLBACK_ORIGIN) {
        return;
      }
      let msg;
      try {
        msg = JSON.parse(event.data) as InlineCallbackWrappedMessage<T>;
      } catch (e) {
        // We can't be sure that 'something' wont send a non-JSON message from the origin so just
        // logging and moving on.
        getRollbarInstance().warn('@flowio/payment-sdk: Received non-JSON message from inline callback listener.');
      }

      if (!isNil(msg) && msg.type === INLINE_CALLBACK_MESSAGE_TYPE) {
        if (msg.success) {
          resolve(msg.payload);
        } else {
          reject(msg.payload);
        }
        if (isNotNil(timeoutId)) {
          window.clearTimeout(timeoutId);
        }
        window.removeEventListener('message', messageHandler);
      }
    };
    timeoutId = window.setTimeout(() => {
      const errorText = '@flowio/payment-sdk: Payment callback never returned.';
      getRollbarInstance().warn(errorText);
      reject(new Error(errorText));
      window.removeEventListener('message', messageHandler);
    }, getTimeoutMsFromNow(options.expiresAt));

    // eslint-disable-next-line prefer-arrow-callback
    window.addEventListener('message', messageHandler);
  });
}
